import * as React from 'react';
import Button from '@mui/material/Button';
import Dialog from '@mui/material/Dialog';
import DialogActions from '@mui/material/DialogActions';
import DialogContent from '@mui/material/DialogContent';
import DialogTitle from '@mui/material/DialogTitle';
import Slide from '@mui/material/Slide';
import { TransitionProps } from '@mui/material/transitions';
import { i18n } from '../../../translate/i18n';
import { TextField, Grid, Divider, Typography, List, ListItem, IconButton, ListItemText, Chip } from '@mui/material';
import { useEffect, useState } from 'react';
import * as Yup from "yup";
import notify from '../../../helpers/toast';
import { INode } from '../manager';
import EmojiDataPicker from '../../EmojiDataPicker';
import { Edge, useReactFlow } from 'reactflow';
import { useConexaBotVariables } from '../../../hooks/useConexaBotVariables';

import DeleteOutlineOutlinedIcon from '@mui/icons-material/DeleteOutlineOutlined';
import AttachFileOutlinedIcon from '@material-ui/icons/AttachFileOutlined';

import SingleSelect from '../../InputStyled/singleSelect';
import AutoComplete from '../General/AutoComplete';
import CustomSwitch from '../General/CustomSwitch';
import CustomChipInput from '../General/CustomChipInput';
import { Box } from '@material-ui/core';

const Transition = React.forwardRef(function Transition(
  props: TransitionProps & { children: React.ReactElement<any, any>; },
  ref: React.Ref<unknown>
) {
  return <Slide direction="up" ref={ref} {...props} />;
});

interface IRequest {
  open: boolean;
  onClose: () => void;
  data: INode['data'] | undefined;
  departments: any[] | null;
  quickReplies: any[] | null;
  conclusionMessages: any[] | null;
  openingHours: any[] | null;
  nodes?: INode[] | undefined;
  edges?: Edge[] | undefined;
}

interface optionProps {
  id: string;
  name: string;
  value: string[];
}

const NodeModal = ({ open, onClose, data, departments, quickReplies, conclusionMessages, openingHours, nodes, edges }: IRequest) => {
  const fullWidth = true;
  const maxWidth = 'sm';
  const inputRef = React.useRef<HTMLInputElement | null>(null);
  const questionInputRef = React.useRef<HTMLInputElement | null>(null);

  const initialState: INode['data'] = {
    id: "",
    label: "",
    type: "start",
    subtype: "start",
    message: "",
    question: "",
    variable: "",
    departmentId: undefined,
    await: "",
    simpleCondition: undefined,
    compoundCondition: undefined,
    quickReplyId: undefined,
    conclusionMessageId: undefined,
    openingHourId: undefined
  };

  const [customNode, setCustomNode] = useState(initialState);
  const [saveAnswer, setSaveAnswer] = useState(false);
  const [handleEdge, setHandleEdge] = useState<string | undefined>(undefined);
  const { setNodes, setEdges } = useReactFlow();
  const { variables, add, remove } = useConexaBotVariables();
  const optionInitialState: optionProps = {
    id: '',
    name: '',
    value: []
  };
  const [optionProps, setOptionProps] = useState<optionProps>(optionInitialState);

  const simpleConditionOptions = [
    { key: "equalTo", name: i18n.t("conexaBots.nodes.modal.operators.equalTo"), requiredValue: true },
    { key: "notEqual", name: i18n.t("conexaBots.nodes.modal.operators.notEqual"), requiredValue: true },
    { key: "contain", name: i18n.t("conexaBots.nodes.modal.operators.contain"), requiredValue: true },
    { key: "doesNotContain", name: i18n.t("conexaBots.nodes.modal.operators.doesNotContain"), requiredValue: true },
    { key: "greaterThan", name: i18n.t("conexaBots.nodes.modal.operators.greaterThan"), requiredValue: true },
    { key: "greatherOrEqualTo", name: i18n.t("conexaBots.nodes.modal.operators.greatherOrEqualTo"), requiredValue: true },
    { key: "lessThan", name: i18n.t("conexaBots.nodes.modal.operators.lessThan"), requiredValue: true },
    { key: "lessOrEqualTo", name: i18n.t("conexaBots.nodes.modal.operators.lessOrEqualTo"), requiredValue: true },
    { key: "isEmpty", name: i18n.t("conexaBots.nodes.modal.operators.isEmpty"), requiredValue: false },
    { key: "isCPF", name: i18n.t("conexaBots.nodes.modal.operators.isCPF"), requiredValue: false },
    { key: "isCEP", name: i18n.t("conexaBots.nodes.modal.operators.isCEP"), requiredValue: false },
    { key: "isCNPJ", name: i18n.t("conexaBots.nodes.modal.operators.isCNPJ"), requiredValue: false },
    { key: "isPhone", name: i18n.t("conexaBots.nodes.modal.operators.isPhone"), requiredValue: false },
  ];

  // Schema base para label
  const baseSchema = {
    label: Yup.string()
      .required(`${i18n.t("conexaBots.nodes.modal.title")} ${i18n.t("forms.required")}`)
      .min(2, `${i18n.t("conexaBots.nodes.modal.title")} ${i18n.t("forms.min")}`)
      .max(100, `${i18n.t("conexaBots.nodes.modal.title")} ${i18n.t("forms.max")}`)
  };

  // Schemas condicionais
  const NodeSchema: any = {
    message: Yup.object().shape({
      ...baseSchema,
      message: Yup.string()
        .required(`${i18n.t("conexaBots.nodes.modal.message")} ${i18n.t("forms.required")}`)
        .min(5, `${i18n.t("conexaBots.nodes.modal.message")} ${i18n.t("forms.min")}`)
        .max(5000, `${i18n.t("conexaBots.nodes.modal.message")} ${i18n.t("forms.max")}`),
    }),
    question: Yup.object().shape({
      ...baseSchema,
      question: Yup.string()
        .required(`${i18n.t("conexaBots.nodes.modal.question")} ${i18n.t("forms.required")}`)
        .min(5, `${i18n.t("conexaBots.nodes.modal.question")} ${i18n.t("forms.min")}`)
        .max(500, `${i18n.t("conexaBots.nodes.modal.question")} ${i18n.t("forms.max")}`),
      variable: Yup.string().when([], {
        is: () => saveAnswer === true,
        then: (schema) =>
          schema.required(`${i18n.t("conexaBots.nodes.modal.variable")} ${i18n.t("forms.required")}`),
        otherwise: (schema) => schema.notRequired()
      })
    }),
    await: Yup.object().shape({
      ...baseSchema,
      await: Yup.string()
        .required(`${i18n.t("conexaBots.nodes.modal.await")} ${i18n.t("forms.required")}`)
        .min(1, `${i18n.t("conexaBots.nodes.modal.await")} ${i18n.t("forms.min")}`)
        .max(4, `${i18n.t("conexaBots.nodes.modal.await")} ${i18n.t("forms.max")}`),
    }),
    transfer: Yup.object().shape({
      ...baseSchema,
      departmentId: Yup.string()
        .required(`${i18n.t("conexaBots.nodes.modal.department.select")} ${i18n.t("forms.required")}`)
    }),
    simpleCondition: Yup.object().shape({
      ...baseSchema,
      simpleCondition: Yup.object().shape({
        variable: Yup.string()
          .required(`${i18n.t("conexaBots.nodes.modal.simpleCondition.variable.select")} ${i18n.t("forms.required")}`),
        operator: Yup.string()
          .required(`${i18n.t("conexaBots.nodes.modal.simpleCondition.operator.select")} ${i18n.t("forms.required")}`),
        value: Yup.string().when([], {
          is: () => checkSimpleConditionRequiredValue(),
          then: (schema) =>
            schema
              .required(`${i18n.t("conexaBots.nodes.modal.value")} ${i18n.t("forms.required")}`)
              .min(1, `${i18n.t("conexaBots.nodes.modal.value")} ${i18n.t("forms.min")}`)
              .max(100, `${i18n.t("conexaBots.nodes.modal.value")} ${i18n.t("forms.max")}`),
          otherwise: (schema) => schema.strip()
        })
      })
    }),
    compoundCondition: Yup.object().shape({
      ...baseSchema,
      compoundCondition: Yup.object().shape({
        variable: Yup.string()
          .required(`${i18n.t("conexaBots.nodes.modal.compoundCondition.variable.select")} ${i18n.t("forms.required")}`),
        options: Yup.array()
          .required(`${i18n.t("conexaBots.messages.OPTION_NOT_FOUND")} ${i18n.t("forms.required")}`)
          .min(1, `${i18n.t("conexaBots.messages.OPTION_NOT_FOUND")} ${i18n.t("forms.required")}`)
      })
    }),
    finish: Yup.object().shape({
      ...baseSchema,
      conclusionMessageId: Yup.string()
        .required(`${i18n.t("conexaBots.nodes.modal.conclusionMessage.select")} ${i18n.t("forms.required")}`)
    }),
    openingHour: Yup.object().shape({
      ...baseSchema,
      openingHourId: Yup.string()
        .required(`${i18n.t("conexaBots.nodes.modal.openingHour.select")} ${i18n.t("forms.required")}`)
    }),
    quickReply: Yup.object().shape({
      ...baseSchema,
      quickReplyId: Yup.string()
        .required(`${i18n.t("conexaBots.nodes.modal.quickReply.title")} ${i18n.t("forms.required")}`)
    }),
  };

  useEffect(() => {
    if (!open || !data) {
      setCustomNode(initialState);
      return;
    }
    setCustomNode(data);
    if (data?.variable) setSaveAnswer(true);

    // verificar elemento que está vinculado
    if (Array.isArray(edges) && edges.length > 0) {
      if (data.type != "end" && ['start', 'compoundCondition', 'simpleCondition', 'openingHour'].includes(data.subtype) == false) {
        const index = edges.findIndex((edge) => edge.source == data.id);
        if (index >= 0) setHandleEdge(edges[index].target);

      }
    }
  }, [open]);

  const handleClose = (event?: any, reason?: any) => {
    if (reason === 'backdropClick') {
      event.preventDefault();
      return;
    }
    setCustomNode(initialState);
    setSaveAnswer(false);
    setOptionProps(optionInitialState);
    setHandleEdge(undefined);
    onClose();
  };

  const handleSave = async () => {
    try {
      if (['message', 'question', 'await', 'transfer', 'simpleCondition', 'compoundCondition', 'finish', 'openingHour', 'quickReply'].includes(customNode.subtype)) {
        await NodeSchema[customNode.subtype].validate(customNode, { abortEarly: false });
      }

      if (customNode.subtype === "compoundCondition" && customNode?.compoundCondition?.options && Array.isArray(customNode.compoundCondition.options)) {
        for (const opt of customNode.compoundCondition.options) {
          if (!opt?.name || opt.name == "") {
            notify('error','Todas opções devem possuir nomes cadastrados.');
            return;
          }
          if (!opt?.value || !Array.isArray(opt.value) || opt.value.length == 0) {
            notify('error', 'Todas opções devem possuir valores cadastrados.')
            return;
          }
        }
      }
    } catch (err: any) {
      notify('error', err?.errors?.[0] || 'generalMessages.INTERNAL_ERROR');
      return;
    }

    let cNode = { ...customNode };

    if (cNode.subtype === "question" && !saveAnswer && cNode.variable) {
      remove(String(cNode.variable));
      cNode.variable = initialState.variable;
    }

    setNodes((nodes) =>
      nodes.map((node) =>
        node.id === cNode.id ? { ...node, data: cNode } : node
      )
    );

    // Se for compoundCondition, força recalculo da largura do node
    if (customNode.subtype === "compoundCondition") {
      setTimeout(() => {
        if (document.getElementById(customNode.id)) {
          const nodeDiv = document.getElementById(customNode.id);
          if (nodeDiv) {
            const originalWidth = nodeDiv.offsetWidth;
            nodeDiv.style.width = `${originalWidth + 1}px`;
          }
        }
      }, 1000);
    }

    // Se houver seleção de vinculos de outros elementos
    if (handleEdge != undefined) {
      const props_tmp = {
        source: `${cNode.id}`,
        sourceHandle: `${cNode.id}-source`,
        target: `${handleEdge}`,
        targetHandle: `${handleEdge}-target`,
        id: `reactflow__edge-${cNode.id}${cNode.id}-source-${handleEdge}${handleEdge}-target`,
        selected: false
      }
      setEdges((edges) => {
        const index = edges.findIndex(
          (edge) => edge.source === props_tmp.source
        );

        if (index === -1) {
          return [...edges, props_tmp];
        }

        const updated = [...edges];
        updated[index] = props_tmp;

        return updated;
      });
    }
    handleClose();
  };

  const checkSimpleConditionRequiredValue = (): boolean => {
    const found = simpleConditionOptions.find(s => s.key == customNode.simpleCondition?.operator);
    return found?.requiredValue == true;
  }

  const handleAddOption = (chip: string, id: string) => {
    setCustomNode((prev: any) => {
      const options = Array.isArray(prev.compoundCondition?.options)
        ? [...prev.compoundCondition.options]
        : [];

      const idx = options.findIndex((option: optionProps) => option.id === id);

      if (idx === -1) return prev;

      const values = options[idx].value || [];

      // Evita duplicidade
      if (values.includes(chip)) return prev;

      options[idx] = {
        ...options[idx],
        value: [...values, chip]
      };

      return {
        ...prev,
        compoundCondition: {
          ...prev.compoundCondition,
          options
        }
      };
    });
  };

  const handleRemoveOption = (chip: string, id: string) => {
    setCustomNode((prev: any) => {
      const options = Array.isArray(prev.compoundCondition?.options)
        ? [...prev.compoundCondition.options]
        : [];

      const idx = options.findIndex((option: optionProps) => option.id === id);

      if (idx === -1) return prev;

      const values = options[idx].value || [];

      const updatedValues = values.filter((value: string) => value !== chip);

      options[idx] = {
        ...options[idx],
        value: updatedValues
      };

      return {
        ...prev,
        compoundCondition: {
          ...prev.compoundCondition,
          options
        }
      };
    });
  };

  const handleAdd = () => {
    const opt = {
      id: Date.now().toString(),
      name: "",
      value: []
    };

    setCustomNode((prev: any) => ({
      ...prev,
      compoundCondition: {
        ...prev?.compoundCondition,
        variable: prev?.compoundCondition?.variable || "",
        options: Array.isArray(prev?.compoundCondition?.options) ? [...prev.compoundCondition.options, opt] : [opt]
      }
    }));
  };

  const handleDelete = (v: optionProps) => {
    let options = customNode.compoundCondition?.options && Array.isArray(customNode.compoundCondition.options) ? customNode.compoundCondition.options : [];
    const idx = options.findIndex((vari: optionProps) => vari.id == v.id);
    if (idx != -1) options.splice(idx, 1);
    setCustomNode((prev) => ({
      ...prev,
      compoundCondition: prev?.compoundCondition ? { ...prev.compoundCondition, options } : { variable: "", options }
    }));
  }

  return (
    <Dialog
      open={open}
      TransitionComponent={Transition}
      keepMounted
      onClose={(event, reason) => handleClose(event, reason)}
      aria-describedby="alert-dialog-slide-contact"
      disableEscapeKeyDown
      fullWidth={fullWidth}
      maxWidth={maxWidth}
    >
      <DialogTitle>
        {i18n.t(`conexaBots.nodes.${customNode.type}.${customNode.subtype}`)}
      </DialogTitle>
      <Divider />
      <DialogContent style={{ maxHeight: '350px', paddingTop: '0' }}>
        {/* Label */}
        <Grid container spacing={2} mt={2}>
          <Grid item xs={12}>
            <TextField
              id="nodeLabel"
              name="nodeLabel"
              label={
                <>
                  {i18n.t("conexaBots.nodes.modal.title")}
                  <Typography variant="h6" component="label" ml={1} color="error">*</Typography>
                </>
              }
              variant="standard"
              fullWidth
              value={customNode.label}
              inputProps={{ maxLength: 100 }}
              size="medium"
              margin="none"
              onChange={(e: any) => {
                const val = e?.currentTarget?.value || '';
                setCustomNode((prev) => ({ ...prev, label: val }))
              }}
            />
          </Grid>
        </Grid>

        {/* edge */}
        {
          customNode.type != "end" && ['start', 'compoundCondition', 'simpleCondition', 'openingHour'].includes(customNode.subtype) == false &&
          <Grid container spacing={2} mt={2} style={{ position: "relative" }}>
            <Grid item xs={12}>
              <SingleSelect
                title={i18n.t("conexaBots.nodes.modal.edges.title")}
                displayText={i18n.t("conexaBots.nodes.modal.edges.select")}
                key="nodeModalEdge"
                id="nodeEdge"
                name="nodeEdge"
                options={Array.isArray(nodes) ? nodes.map((d: any) => { return { key: d["data"].id, value: `${d["data"].id} - ${d["data"].label}` } }) : []}
                disabled={false}
                onSelected={(value: any) => setHandleEdge(value)}
                selected={handleEdge || null}
                closeOnSelect={true}
              />
            </Grid>
          </Grid>
        }
        {/* Subtype: Message */}
        {["message", "transfer"].includes(customNode.subtype) && (
          <>
            {
              variables.length > 0
              &&
              <Grid container spacing={2} mt={1}>
                <Grid item xs={12}>
                  {variables.map(v => {
                    return (
                      <Chip
                        variant="filled"
                        color="primary"
                        label={v}
                        style={{ marginRight: "10px" }}
                        onClick={() => {
                          const completeVar = `{{${v}}}`;
                          if (!inputRef.current) return;
                          let val = customNode?.message || inputRef?.current.value || "";

                          const input = inputRef.current;
                          const start = input.selectionStart ?? val.length;
                          const end = input.selectionEnd ?? val.length;

                          // Inserir emoji no texto
                          const newValue = val.slice(0, start) + completeVar + val.slice(end);

                          setCustomNode((prev) => ({ ...prev, message: newValue }));

                          // Garantir que o cursor vá para logo após o emoji
                          requestAnimationFrame(() => {
                            input.focus();
                            const newCursorPos = start + completeVar.length;
                            input.setSelectionRange(newCursorPos, newCursorPos);
                          });
                        }}
                      />
                    )
                  })}
                </Grid>
              </Grid>
            }
            <Grid container spacing={2} mt={0} style={{ position: "relative" }}>
              <Grid item xs={12}>
                <Box style={{
                  display: "block",
                  width: "fit-content",
                  position: "absolute",
                  top: "20px",
                  right: "10px",
                  zIndex: "10"
                }}>
                  <EmojiDataPicker
                    onEmojiSelect={(emoji: any) => {
                      if (!inputRef.current) return;
                      const val = customNode.message || inputRef?.current.value || "";
                      const start = inputRef.current.selectionStart ?? val.length;
                      const end = inputRef.current.selectionEnd ?? val.length;
                      const newValue = val.slice(0, start) + emoji + val.slice(end);
                      setCustomNode((prev) => ({ ...prev, message: newValue }));
                    }}
                    disabled={false}
                  />
                </Box>
                <TextField
                  id="nodeMessage"
                  name="nodeMessage"
                  inputRef={inputRef}
                  label={
                    <>
                      {i18n.t("conexaBots.nodes.modal.message")}
                      {["finish", "transfer"].includes(customNode.subtype) == false && <Typography variant="h6" component="label" ml={1} color="error">*</Typography>}
                    </>
                  }
                  variant="outlined"
                  fullWidth
                  value={customNode.message}
                  multiline
                  rows={3}
                  inputProps={{ maxLength: 5000 }}
                  onChange={(e) => {
                    const val = e?.currentTarget?.value || '';
                    setCustomNode((prev) => ({ ...prev, message: val }))
                  }}
                />
              </Grid>
            </Grid>
          </>
        )}

        {/* Subtype: Question */}
        {customNode.subtype === "question" && (
          <>
            {
              variables.length > 0
              &&
              <Grid container spacing={2} mt={1}>
                <Grid item xs={12}>
                  {variables.map(v => {
                    return (
                      <Chip
                        variant="filled"
                        color="primary"
                        label={v}
                        style={{ marginRight: "10px" }}
                        onClick={() => {
                          const completeVar = `{{${v}}}`;
                          if (!questionInputRef.current) return;
                          let val = customNode.question || questionInputRef?.current.value || "";

                          const input = questionInputRef.current;
                          const start = input.selectionStart ?? val.length;
                          const end = input.selectionEnd ?? val.length;

                          // Inserir emoji no texto
                          const newValue = val.slice(0, start) + completeVar + val.slice(end);

                          setCustomNode((prev) => ({ ...prev, question: newValue }));

                          // Garantir que o cursor vá para logo após o emoji
                          requestAnimationFrame(() => {
                            input.focus();
                            const newCursorPos = start + completeVar.length;
                            input.setSelectionRange(newCursorPos, newCursorPos);
                          });
                        }}
                      />
                    )
                  })}
                </Grid>
              </Grid>
            }
            <Grid container spacing={2} mt={0} style={{ position: "relative" }}>
              <Grid item xs={12}>
                <Box style={{
                  display: "block",
                  width: "fit-content",
                  position: "absolute",
                  top: "20px",
                  right: "10px",
                  zIndex: "10"
                }}>
                  <EmojiDataPicker
                    onEmojiSelect={(emoji: any) => {
                      if (!questionInputRef.current) return;
                      let val = customNode.question || questionInputRef?.current.value || "";
                      const start = questionInputRef.current.selectionStart ?? val.length;
                      const end = questionInputRef.current.selectionEnd ?? val.length;
                      const newValue = val.slice(0, start) + emoji + val.slice(end);
                      setCustomNode((prev) => ({ ...prev, question: newValue }));
                    }}
                    disabled={false}
                  />
                </Box>
                <TextField
                  id="nodeQuestion"
                  name="nodeQuestion"
                  inputRef={questionInputRef}
                  label={
                    <>
                      {i18n.t("conexaBots.nodes.modal.question")}
                      <Typography variant="h6" component="label" ml={1} color="error">*</Typography>
                    </>
                  }
                  variant="outlined"
                  fullWidth
                  value={customNode.question}
                  multiline
                  rows={2}
                  inputProps={{ maxLength: 1000 }}
                  onChange={(e) => {
                    const val = e?.currentTarget?.value || '';
                    setCustomNode((prev) => ({ ...prev, question: val }))
                  }}
                />
              </Grid>
            </Grid>

            <Grid container spacing={2} mt={2}>
              <Grid item xs={12}>
                <CustomSwitch
                  id="nodeSaveAnswer"
                  name="nodeSaveAnswer"
                  leftText={i18n.t("conexaBots.nodes.modal.saveAnswer")}
                  value={saveAnswer}
                  onClick={() => setSaveAnswer(!saveAnswer)}
                />
              </Grid>
            </Grid>

            {saveAnswer && (
              <Grid container spacing={2} mt={0}>
                <Grid item xs={12}>
                  <AutoComplete
                    options={variables.map((v: string) => ({ label: v, id: v }))}
                    title={i18n.t("conexaBots.nodes.modal.variable")}
                    key="nodeQuestionVariable"
                    value={
                      customNode.variable
                        ? { label: customNode.variable, id: customNode.variable }
                        : null
                    }
                    onChange={(event, value) => {
                      if (value) {
                        // se já houver um custom node de variavel, remover o atual para adicionar o novo
                        if (customNode?.variable) remove(customNode.variable);
                        if (!variables.find((v) => v == value.label)) {
                          add(value.label);
                        }
                        setCustomNode((prev) => ({ ...prev, variable: value.label }));
                      }
                    }}
                  />
                </Grid>
              </Grid>
            )}
          </>
        )}

        {/* Subtype: Transfer */}
        {customNode.subtype === "transfer" && (
          <>
            <Grid container spacing={2} mt={2} style={{ position: "relative" }}>
              <Grid item xs={12}>
                <SingleSelect
                  title={
                    <>
                      {i18n.t("conexaBots.nodes.modal.department.title")}
                      <Typography variant="h6" component="label" ml={1} color="error">*</Typography>
                    </>
                  }
                  displayText={i18n.t("conexaBots.nodes.modal.department.select")}
                  key="nodeModalTransfer"
                  id="nodeTransfer"
                  name="nodeTransfer"
                  options={Array.isArray(departments) ? departments.map((d: any) => { return { key: d.id, value: d.name } }) : []}
                  disabled={false}
                  onSelected={(value: any) => setCustomNode((prev) => ({ ...prev, departmentId: value }))}
                  selected={customNode?.departmentId || null}
                  closeOnSelect={true}
                />
              </Grid>
            </Grid>
          </>
        )}

        {/* Subtype: Finish */}
        {customNode.subtype === "finish" && (
          <>
            <Grid container spacing={2} mt={2} style={{ position: "relative" }}>
              <Grid item xs={12}>
                <SingleSelect
                  title={
                    <>
                      {i18n.t("conexaBots.nodes.modal.conclusionMessage.title")}
                      <Typography variant="h6" component="label" ml={1} color="error">*</Typography>
                    </>
                  }
                  displayText={i18n.t("conexaBots.nodes.modal.conclusionMessage.select")}
                  key="nodeModalConclusionMessage"
                  id="nodeConclusionMessage"
                  name="nodeConclusionMessage"
                  options={Array.isArray(conclusionMessages) ? conclusionMessages.map((c: any) => { return { key: c.id, value: c.shortcut } }) : []}
                  disabled={false}
                  onSelected={(value: any) => setCustomNode((prev) => ({ ...prev, conclusionMessageId: value }))}
                  selected={customNode?.conclusionMessageId || null}
                  closeOnSelect={true}
                />
              </Grid>
            </Grid>
          </>
        )}

        {/* Subtype: QuickReply */}
        {customNode.subtype === "quickReply" && (
          <>
            <Grid container spacing={2} mt={2} style={{ position: "relative" }}>
              <Grid item xs={12}>
                <SingleSelect
                  title={
                    <>
                      {i18n.t("conexaBots.nodes.modal.quickReply.title")}
                      <Typography variant="h6" component="label" ml={1} color="error">*</Typography>
                    </>
                  }
                  displayText={i18n.t("conexaBots.nodes.modal.quickReply.select")}
                  key="nodeModalQuickReply"
                  id="nodeQuickReply"
                  name="nodeQuickReply"
                  options={
                    Array.isArray(quickReplies)
                      ?
                      quickReplies.map((q: any) => {
                        if (q?.driveUrl) {
                          return { key: q.id, value: <><AttachFileOutlinedIcon style={{ marginRight: 10 }} />{q.shortcut}</> }
                        } else {
                          return { key: q.id, value: q.shortcut }
                        }
                      })
                      :
                      []
                  }
                  disabled={false}
                  onSelected={(value: any) => setCustomNode((prev) => ({ ...prev, quickReplyId: value }))}
                  selected={customNode?.quickReplyId || null}
                  closeOnSelect={true}
                />
              </Grid>
            </Grid>
          </>
        )}

        {/* Subtype: Await */}
        {customNode.subtype == "await" && (
          <Grid container spacing={2} mt={2} style={{ position: "relative" }}>
            <Grid item xs={12}>
              <TextField
                id="nodeAwait"
                name="nodeAwait"
                label={
                  <>
                    {i18n.t("conexaBots.nodes.modal.await")}
                    <Typography variant="h6" component="label" ml={1} color="error">
                      *
                    </Typography>
                  </>
                }
                variant="standard"
                margin="none"
                fullWidth
                value={customNode.await}
                inputProps={{ maxLength: 4, inputMode: "numeric", pattern: "[0-9]*" }}
                onChange={(e) => {
                  let value = e.currentTarget.value.replace(/\D/g, ""); // remove tudo que não é número

                  if (value) {
                    let num = parseInt(value, 10);
                    if (num > 3600) num = 3600;
                    value = num.toString();
                  }

                  setCustomNode((prev) => ({ ...prev, await: value }));
                }}
              />
            </Grid>
          </Grid>
        )}

        {/* Subtype: SimpleCondition */}
        {customNode.subtype == "simpleCondition" && (
          <>
            <Grid container spacing={2} mt={2}>
              <Grid item xs={12}>
                <SingleSelect
                  title={i18n.t("conexaBots.nodes.modal.simpleCondition.variable.title")}
                  displayText={i18n.t("conexaBots.nodes.modal.simpleCondition.variable.select")}
                  key="simpleConditionVariable"
                  options={Array.isArray(variables) ? variables.map((v: string) => { return { key: v, value: v } }) : []}
                  disabled={false}
                  onSelected={(value: any) => {
                    setCustomNode((prev) => ({
                      ...prev,
                      simpleCondition: prev?.simpleCondition ? { ...prev.simpleCondition, variable: value } : { variable: value, operator: "", value: "" }
                    }))
                  }}
                  selected={customNode?.simpleCondition?.variable || null}
                  closeOnSelect={true}
                />
              </Grid>
            </Grid>
            <Grid container spacing={2} mt={2}>
              <Grid item xs={12}>
                <SingleSelect
                  title={i18n.t("conexaBots.nodes.modal.simpleCondition.operator.title")}
                  displayText={i18n.t("conexaBots.nodes.modal.simpleCondition.operator.select")}
                  key="simpleConditionOperator"
                  options={
                    simpleConditionOptions
                      .slice()
                      .sort((a: any, b: any) => a.name.localeCompare(b.name))
                      .map((v: any) => ({ key: v.key, value: v.name }))
                  }
                  disabled={false}
                  onSelected={(value: any) => {
                    setCustomNode((prev) => ({
                      ...prev,
                      simpleCondition: prev?.simpleCondition ? { ...prev.simpleCondition, operator: value } : { variable: "", operator: value, value: "" }
                    }))
                  }}
                  selected={customNode?.simpleCondition?.operator || null}
                  closeOnSelect={true}
                />
              </Grid>
            </Grid>
            {
              checkSimpleConditionRequiredValue() == true &&
              <Grid container spacing={2} mt={2}>
                <Grid item xs={12}>
                  <TextField
                    id="nodeSimpleConditionValue"
                    name="nodeSimpleConditionValue"
                    label={
                      <>
                        {i18n.t("conexaBots.nodes.modal.value")}
                        <Typography variant="h6" component="label" ml={1} color="error">*</Typography>
                      </>
                    }
                    variant="standard"
                    margin="none"
                    fullWidth
                    value={customNode.simpleCondition?.value}
                    inputProps={{ maxLength: 100 }}
                    onChange={(e) => {
                      const val = e?.currentTarget?.value || '';
                      setCustomNode((prev) => ({
                        ...prev,
                        simpleCondition: prev?.simpleCondition ? { ...prev.simpleCondition, value: val } : { variable: "", operator: "", value: val }
                      }))
                    }}
                  />
                </Grid>
              </Grid>
            }
          </>
        )}

        {/* Subtype: CompoundCondition */}
        {customNode.subtype == "compoundCondition" && (
          <>
            <Grid container spacing={2} mt={2}>
              <Grid item xs={12}>
                <SingleSelect
                  title={i18n.t("conexaBots.nodes.modal.compoundCondition.variable.title")}
                  displayText={i18n.t("conexaBots.nodes.modal.compoundCondition.variable.select")}
                  key="compoundConditionVariable"
                  options={Array.isArray(variables) ? variables.map((v: string) => { return { key: v, value: v } }) : []}
                  disabled={false}
                  onSelected={(value: any) => {
                    setCustomNode((prev) => ({
                      ...prev,
                      compoundCondition: prev?.compoundCondition ? { ...prev.compoundCondition, variable: value } : { variable: value, options: [] }
                    }))
                  }}
                  selected={customNode?.compoundCondition?.variable || null}
                  closeOnSelect={true}
                />
              </Grid>
            </Grid>
            {/* renderizar itens a cada edição */}
            {
              (customNode?.compoundCondition?.options && Array.isArray(customNode.compoundCondition.options) && customNode.compoundCondition.options.length > 0)
              &&
              customNode.compoundCondition.options.map(op => {
                return (
                  <Box
                    style={{
                      padding: "20px 10px",
                      background: "#b0bec5",
                      borderRadius: "10px",
                      position: "relative",
                      marginTop: "10px"
                    }}
                  >
                    <IconButton
                      aria-label="delete"
                      color="error"
                      onClick={() => handleDelete(op)}
                      style={{
                        position: "absolute",
                        top: "10px",
                        right: "10px"
                      }}
                    >
                      <DeleteOutlineOutlinedIcon />
                    </IconButton>
                    <Grid container spacing={2} mt={2}>
                      <Grid item xs={12}>
                        <TextField
                          id={`nodeCompoundConditionOptionName-${op.id}`}
                          name={`nodeCompoundConditionOptionName-${op.id}`}
                          label={
                            <>
                              {i18n.t("conexaBots.nodes.modal.compoundCondition.optionName")}
                              <Typography
                                variant="h6"
                                component="label"
                                ml={1}
                                style={{ lineHeight: "normal" }}
                                color="error"
                              >
                                *
                              </Typography>
                            </>
                          }
                          variant="standard"
                          margin="none"
                          fullWidth
                          value={op?.name}
                          onChange={(e) => {

                            let options = customNode.compoundCondition?.options && Array.isArray(customNode.compoundCondition.options) ? customNode.compoundCondition.options : [];
                            const idx = options.findIndex((vari: optionProps) => vari.id == op.id);
                            if (idx == -1) return;

                            const rawValue = e.currentTarget.value;

                            // 1. Normaliza e remove acentos (NFD separa acentos das letras)
                            let sanitizedValue = rawValue.normalize("NFD").replace(/[\u0300-\u036f]/g, "");

                            // 2. Substitui espaço por underline
                            sanitizedValue = sanitizedValue.replace(/\s+/g, "_");

                            // 3. Remove qualquer caractere que não seja letra, número ou underline
                            sanitizedValue = sanitizedValue.replace(/[^a-zA-Z0-9_]/g, "");
                            options[idx].name = sanitizedValue;

                            setCustomNode((prev: any) => ({
                              ...prev,
                              compoundCondition: { ...prev.compoundCondition, options }
                            }));
                          }}
                          inputProps={{ maxLength: 20 }}
                        />
                      </Grid>
                    </Grid>
                    <Grid container spacing={2} mt={2}>
                      <Grid item xs={12}>
                        <CustomChipInput
                          id={op.id}
                          name={`nodeCompoundConditionOptionValue-${op.id}`}
                          value={op?.value || []}
                          onAdd={handleAddOption}
                          onDelete={handleRemoveOption}
                          label={
                            <>
                              {i18n.t("conexaBots.nodes.modal.compoundCondition.optionValue")}
                              <Typography variant="h6" component="label" ml={1} style={{ lineHeight: 'normal' }} color="error">*</Typography>
                            </>
                          }
                          placeholder={i18n.t("conexaBots.nodes.modal.compoundCondition.optionValuePlaceholder")}
                        />
                      </Grid>
                    </Grid>
                  </Box>
                )
              })
            }
            <Grid container spacing={2} mt={2}>
              <Grid item xs={12}>
                <Button
                  onClick={handleAdd}
                  variant="contained"
                  size="small"
                  color="primary"
                  fullWidth={true}
                >
                  ADICIONAR NOVA OPÇÃO
                </Button>
              </Grid>
            </Grid>
          </>
        )}

        {/* Subtype: OpeningHour */}
        {customNode.subtype === "openingHour" && (
          <>
            <Grid container spacing={2} mt={2} style={{ position: "relative" }}>
              <Grid item xs={12}>
                <SingleSelect
                  title={i18n.t("conexaBots.nodes.modal.openingHour.title")}
                  displayText={i18n.t("conexaBots.nodes.modal.openingHour.select")}
                  key="nodeModalOpeningHour"
                  id="nodeOpeningHour"
                  name="nodeOpeningHour"
                  options={Array.isArray(openingHours) ? openingHours.map((o: any) => { return { key: o.id, value: o.name } }) : []}
                  disabled={false}
                  onSelected={(value: any) => setCustomNode((prev) => ({ ...prev, openingHourId: value }))}
                  selected={customNode?.openingHourId || null}
                  closeOnSelect={true}
                />
              </Grid>
            </Grid>
          </>
        )}
      </DialogContent>

      <DialogActions>
        <Button onClick={handleClose} variant="contained" size="small" color="error">
          {i18n.t("buttons.cancel")}
        </Button>
        <Button onClick={handleSave} variant="contained" size="small" color="success">
          {i18n.t("buttons.save")}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default NodeModal;
